import React, { useEffect, useState } from "react";
import MuiPaper from "Components/MuiPaper/MuiPaper";
import Translator from "Languages/Utility/Translator";
import { satc_priceService } from "Constants/TableColumns";
import RemoteTable from "Components/RemoteTable/RemoteTable";
import { useTranslation } from "react-i18next";
import { useSetState } from "Helpers/Hooks";
import MuiAutoComplete from "Components/MuiAutoComplete/MuiAutoComplete";
import Grid from "@material-ui/core/Grid";
import { read } from "Helpers/LocalStorage";
import { Skeleton } from "@material-ui/lab";
import { useDispatch, useSelector } from "react-redux";
import {
  getAccountingPriceService,
  getActivePeriods,
  getServiceCombo,
} from "Stores/actions";
import BackDropLoading from "Components/BackDropLoading2/BackDropLoading";
import AddServices from "./AddServices";
import PageTitle from "Components/PageTitle_IconCircle/PageTitle";

export default function Services() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [sizePerPage, setSizePerPage] = useState(10);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [tblData, setTblData] = useState();
  const [columns, setColumns] = useState([]);
  const {
    accountPriceService,
    activePeriods,
    serviceCombo,
    getPriceServiceAgain,
    pagination,
  } = useSelector((state) => ({
    accountPriceService: state.saAccounts.accountPriceService,
    activePeriods: state.saAccounts.activePeriods,
    serviceCombo: state.saAccounts.serviceCombo,
    getPriceServiceAgain: state.saAccounts.getPriceServiceAgain,
    pagination: state.saAccounts.pagination,
  }));
  const [controls, setControls] = useSetState({
    periods: {
      type: "select",
      selectItems: [],
      value: "",
      validation: null,
    },
    services: {
      type: "select",
      selectItems: [],
      value: "",
      validation: null,
    },
  });

  useEffect(() => {
    onEnter();
  }, []);

  const onEnter = async () => {
    setLoading(true);
    const _columns = [...satc_priceService];
    _columns[8].hidden = !read("FormPriceSuperAdmin").accessUpdate;
    _columns[9].hidden = !read("FormPriceSuperAdmin").accessDelete;
    setColumns(_columns);
    await getPeriods();
    await getServiceCombos();
    await getPrices(1, sizePerPage);
  };

  const getPrices = async (_page, _sizePerPage, dataSearch = null) => {
    setLoading(true);
    return new Promise(async (resolve) => {
      const params = {
        KeyGroup: read("groupAKey"),
        KeyUser: read("userAKey"),
        Limit: _sizePerPage,
        Page: _page,
        ...dataSearch,
      };
      await dispatch(getAccountingPriceService(params));
      setLoading(false);
      resolve(true);
    });
  };

  const getPeriods = async () => {
    return new Promise(async (resolve) => {
      const params = {
        KeyGroup: read("groupAKey"),
        KeyUser: read("userAKey"),
      };
      await dispatch(getActivePeriods(params));
      resolve(true);
    });
  };

  const getServiceCombos = async () => {
    return new Promise(async (resolve) => {
      const params = {
        KeyGroup: read("groupAKey"),
        KeyUser: read("userAKey"),
      };
      await dispatch(getServiceCombo(params));
      resolve(true);
    });
  };

  useEffect(() => {
    if (accountPriceService) {
      setTblData(accountPriceService);
      setSizePerPage(pagination.Limit);
      setCurrentPage(pagination.Page);
      setTotal(pagination.Total);
    }
  }, [accountPriceService]);

  useEffect(() => {
    if (activePeriods) {
      const defArr = [{ label: t("chooseOnePeriod"), value: -1 }];
      const selectItems = activePeriods.map((child) => {
        return {
          label: child.Name,
          value: child.Id,
        };
      });
      setControls({ periods: { selectItems: defArr.concat(selectItems) } });
      setControls({ periods: { value: defArr[0] } });
    }
  }, [activePeriods]);

  useEffect(() => {
    if (serviceCombo) {
      const defArr = [{ label: t("chooseOneService"), value: -1 }];
      const selectItems = serviceCombo.map((child) => {
        return {
          label: child.Name,
          value: child.Id,
        };
      });
      setControls({ services: { selectItems: defArr.concat(selectItems) } });
      setControls({ services: { value: defArr[0] } });
    }
  }, [serviceCombo]);

  const onTableChange = async (type, { page, sizePerPage }) => {
    if (type === "pagination") {
      await getPrices(page, sizePerPage);
    }
  };

  const autoCompleteHandler = async (e, value) => {
    const controlName = e.target.id.split("-")[0];
    setControls({ [controlName]: { value: value } });
    await tblRefresh(
      1,
      sizePerPage,
      controlName === "services" ? value.value : null,
      controlName === "periods" ? value.value : null
    );
  };

  useEffect(() => {
    if (getPriceServiceAgain) {
      (async () => {
        await tblRefresh(currentPage, sizePerPage);
      })();
    }
  }, [getPriceServiceAgain]);

  const tblRefresh = async (
    innerPage,
    innerSizePerPage,
    service = null,
    period = null
  ) => {
    let dataSearch = {};
    const { services, periods } = controls;
    if (service) {
      if (service !== -1) dataSearch.ServiceId = service;
      if (periods.value.value !== -1) dataSearch.PeriodId = periods.value.value;
    } else if (period) {
      if (period !== -1) dataSearch.PeriodId = period;
      if (services.value.value !== -1)
        dataSearch.ServiceId = services.value.value;
    } else {
      if (services.value.value !== -1)
        dataSearch.ServiceId = services.value.value;
      if (periods.value.value !== -1) dataSearch.PeriodId = periods.value.value;
    }
    await getPrices(innerPage, innerSizePerPage, dataSearch);
  };

  return (
    <>
      <BackDropLoading loading={loading} />
      <div className="page-top-title-button my-3">
        <PageTitle title={<Translator id={"services"} />} />
        <div className="top-buttons">
          <AddServices disabled={loading} />
        </div>
      </div>
      <MuiPaper>
        <Grid container spacing={2} className="mb-2">
          {Object.keys(controls).map((key) => {
            return (
              <>
                {controls[key].type === "select" && (
                  <Grid item md={3} sm={6} xs={12}>
                    {!loading ? (
                      <MuiAutoComplete
                        autoSelect={true}
                        options={controls[key].selectItems}
                        label={<Translator id={key} />}
                        value={controls[key].value}
                        onChange={autoCompleteHandler}
                        id={key}
                      />
                    ) : (
                      <Skeleton
                        variant="text"
                        className="p-2"
                        animation="wave"
                      />
                    )}
                  </Grid>
                )}
              </>
            );
          })}
        </Grid>
        {columns && columns.length !== 0 && (
          <RemoteTable
            data={tblData}
            columns={columns}
            page={currentPage}
            sizePerPage={sizePerPage}
            totalSize={total}
            onTableChange={onTableChange}
          />
        )}
      </MuiPaper>
    </>
  );
}
